import React, { useState, useEffect } from 'react';
import { Country, State, City } from 'country-state-city';
import styled from 'styled-components';
import { useAuthStore } from '../store/store';
import { useNavigate } from 'react-router-dom';
import { useCartContext } from "../context/cart_context";
import { toast, Toaster } from 'react-hot-toast';
import {toTitleCase, normalize, normalizeNoSpaces } from "../Helpers/stringTitlecase.js";
import AddressForm from '../user/AddressForm';
import AddressList from './Checkout.js/AddressList';
import PaymentSection from './Checkout.js/PaymentSection';
import CartSummary from './Checkout.js/CartSummary';
import axios from 'axios';


const Checkout = () => {

  // -----------------------------
  // ADDRESS STATE
  // -----------------------------

  const [countries] = useState(
    Country.getAllCountries()
  );

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [selectedCountry, setSelectedCountry] =
    useState(null);

  const [selectedState, setSelectedState] =
    useState(null);

  const [formAddress, setFormAddress] = useState({
    name: '',
    street_add: '',
    city: '',
    state_province: '',
    zipcode: '',
    country: ''
  });

  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] =
    useState(null);


  // -----------------------------
  // AUTH + NAVIGATION
  // -----------------------------

  const { auth } = useAuthStore();
  const navigate = useNavigate();


  // -----------------------------
  // CART
  // -----------------------------

  const {
    cart,
    clearCart,
    total_price,
    shippingCharges
  } = useCartContext();


  // -----------------------------
  // PAYMENT
  // -----------------------------

  const [paymentMethod, setPaymentMethod] =
    useState(null);


  // -----------------------------
  // COUNTRY CHANGE
  // -----------------------------

  const handleCountryChange = (country) => {

    setSelectedCountry(country);
    setStates(
      State.getStatesOfCountry(
        country.isoCode
      )
    );

    setCities([]);
    setSelectedState(null);
    setFormAddress((prev) => ({
      ...prev,
      country: country.name,
      isdCode: country.phonecode,
      state_province: '',
      city: ''
    }));
  };


  // -----------------------------
  // STATE CHANGE
  // -----------------------------

  const handleStateChange = (state) => {
    setSelectedState(state);
    setCities(
      City.getCitiesOfState(
        selectedCountry.isoCode,
        state.isoCode
      )
    );

    setFormAddress((prev) => ({
      ...prev,
      state_province: state.name,
      city: ''
    }));
  };


  // -----------------------------
  // FORM CHANGE
  // -----------------------------

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormAddress((prev) => ({
      ...prev,
      [id]: toTitleCase(value)
    }));
  };


  // -----------------------------
  // RESET ADDRESS FORM
  // -----------------------------

  const handleReset = () => {

    setFormAddress({
      name: auth?.user?.username || '',
      street_add: '',
      city: '',
      state_province: '',     
      country: '',
      zipcode: "",
      phone: "",
      isdCode: ""
      });

    setSelectedCountry(null);
    setSelectedState(null);
    setStates([]);
    setCities([]);
  };


  // -----------------------------
  // FETCH EXISTING ADDRESSES
  // -----------------------------

  const fetchAddresses = async () => {

    try {

      const token =
        auth?.token ||
        localStorage.getItem("token");

      const response = await fetch(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/address`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          "Failed to fetch addresses"
        );
      }

      const data = await response.json();
      setAddresses(data);
    } catch (error) {
      console.error(
        "Error loading addresses:",
        error
      );
      toast.error(
        "Unable to load addresses"
      );
    }
  };


  // -----------------------------
  // LOAD ADDRESSES
  // -----------------------------

  useEffect(() => {
    fetchAddresses();
    if (auth?.user?.username) {
      setFormAddress((prev) => ({
        ...prev,
        name: auth.user.username
      }));
    }
  }, []);


  // -----------------------------
  // ADD ADDRESS
  // -----------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {

      const token =
        auth?.token ||
        localStorage.getItem("token");

      const response = await fetch(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/address`,
        { method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(formAddress)
        }
      );

      if (!response.ok) {
        throw new Error(
          "Address creation failed"
        );
      }

      const newAddress =
        await response.json();

      setAddresses((prev) => [
        ...prev,
        newAddress
      ]);

      toast.success(
        "Address added successfully"
      );

      await fetchAddresses();
      handleReset();

    } catch (error) {
      console.error(error);
      toast.error(
        "Failed to add address"
      );
    }
  };


  // -----------------------------
  // SELECT EXISTING ADDRESS
  // -----------------------------

  const handleAddress = (index) => {

    const chosen = addresses[index];

    setSelectedAddress(chosen);

    setFormAddress(chosen);


    const countryObj =
      Country.getAllCountries().find(
        (country) =>
          country.name === chosen.country
      );


    if (countryObj) { 

      setSelectedCountry(countryObj);

      const countryStates =
        State.getStatesOfCountry(
          countryObj.isoCode
        );

      setStates(countryStates);


      const stateObj =
        countryStates.find(
          (state) =>
            state.name ===
            chosen.state_province
        );


      if (stateObj) {

        setSelectedState(stateObj);

        setCities(
          City.getCitiesOfState(
            countryObj.isoCode,
            stateObj.isoCode
          )
        );

      }
    }
  };


  // -----------------------------
  // PAYMENT
  // -----------------------------

  const handlePayment = (e) => {

    setPaymentMethod(
      e.target.value
    );
  };


  // -----------------------------
  // PLACE ORDER
  // -----------------------------

  const handleOrderNow = async () => {
    try {
      if (!selectedAddress) {
        toast.error(
          "Please select an address"
        );
        return;
      }

      if (!paymentMethod) {
        toast.error(
          "Please select a payment method"
        );
        return;
      }


      const token =
        auth?.token ||
        localStorage.getItem("token");


      const orderData = {

        address: selectedAddress._id, 
        paymentMethod,

        products: cart.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          price: item.price
        })),

        shippingCharge: shippingCharges,       
        orderTotal: total_price + shippingCharges
        };
        console.log("OrderData being sent:", orderData);

      const response = await fetch(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/orders`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
            },
          body: JSON.stringify(orderData)
        }
      );

      if (!response.ok) {
        throw new Error(
         "Order creation failed"
        );
      }

      const newOrder =
        await response.json();

      toast.success(
        "Order placed successfully!", {duration: 3000}
      );

      clearCart();

      navigate(
        `/orderprint/${newOrder._id}`
      );


    } catch (error) {
      console.error(error);
      toast.error(
        "Failed to place order"
      );
    }
  };


  // -----------------------------
  // PAGE
  // -----------------------------



  const handleUpdate = async () => {
  try {
    const token = auth?.token;
    await axios.put(`${process.env.REACT_APP_SERVER_DOMAIN}/api/address/${formAddress._id}`, formAddress, {
      headers: { Authorization: `Bearer ${token}` },
    });
    toast.success("Address updated successfully");
    await fetchAddresses();   // ✅ refresh list immediately
  } catch (error) {
    toast.error("Failed to update address");
  }
};
  return (

    <Wrapper>

      <Toaster
        position="top-right"
        reverseOrder={false}
      />


      <div className="container">

        <h2>Checkout</h2>
        <div className="checkout-container">
          {/* LEFT SIDE */}
          <div className="address-section">           
            <AddressList            
              addresses={addresses}
              selectedAddress={selectedAddress}
              handleAddress={handleAddress}
            />
            
            <div className="shipping-address">
              <fieldset>
               <legend>Enter a New delivery Address</legend>             
              <p className="address-subtext"> (Select Address to Edit or Enter New Address) </p>
              <AddressForm
                formAddress={formAddress}
                setFormAddress={setFormAddress}
                handleChange={handleChange}
                handleCountryChange={handleCountryChange}
                handleStateChange={handleStateChange}
                handleSubmit={handleSubmit}
                handleReset={handleReset}
                handleUpdate={handleUpdate}
                countries={countries}
                states={states}
                cities={cities}
                selectedCountry={selectedCountry}
                selectedState={selectedState}
                auth={auth}
                submitLabel="Add"
              />
              </fieldset>
            </div>  
             <PaymentSection
              paymentMethod={paymentMethod}
              handlePayment={handlePayment}
            />
          </div>

          {/* RIGHT SIDE */}

          <CartSummary
            cart={cart}
            total_price={total_price}
            shippingCharges={shippingCharges}
            onOrderNow={handleOrderNow}
          />
        </div>
      </div>
    </Wrapper>
  );
};


const Wrapper = styled.section`

  background: #fff;
  padding: 5rem;

  .checkout-container {
    display: grid;
    grid-template-columns: 5.5fr 4.5fr;
    gap: 3rem;
    padding: 1rem;
    border-radius: 0.8rem;
    box-shadow: 0 2px 10px  rgba(0,0,0,0.1);
    }


  .address-section {
    display: block;
    margin-top: 0rem;
    padding: 0rem 1rem;
    }

  .shipping-address {    
    padding-top: 1rem;
    }

  .Quantity-style {   
    padding: 0.5rem ;
    margin-bottom: 1rem;
    }
    
 .cart-table { 
  font-size: 1.3rem; 
  } 
  
  .cart-item-info p {
   font-size: 1.3rem; 
  }

  .address-subtext { 
    font-size: 1.2rem;            
    color: #692ce2; 
    margin-top: 0; 
    } 

  legend{
    font-size: 1.5rem;
    }
 
    
.shipping-address fieldset{
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding : 0.5rem 1.5rem;
  }

@media (max-width: ${({ theme }) =>theme.media.mobile}) {
    padding: 1rem;

  .checkout-container {
    display: block;
    }

  h2{
    font-size: 3rem;}
  
  Button{
    width:6rem;   
    }
  }
    
`;


export default Checkout;

